import { SupplierDefaultPrices, SupplierDefaultValues } from 'types';
import styles from './OrganizationDefaultValues.module.scss';
import * as yup from 'yup';
import {
    FormInputGroup,
    FormProvider,
    FormSubmitButton,
    FormText,
} from 'components/form';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Tooltip,
} from '@mui/material';
import { updateSupplierDefaultValues } from 'lib/firebase';
import Save from '@mui/icons-material/Save';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { HelpOutline } from '@mui/icons-material';
import { deviceAlertTooltips } from 'lib/constants';

export interface OrganizationDefaultValuesProps {
    supplierId: string;
    supplierDefaultValues: SupplierDefaultValues;
}

const defaultValuesSchema = yup.object({
    thresh1: yup.number().required(),
    thresh2: yup.number().required(),
    tcov: yup.number().required(),
    defaultPrices: yup.object<{
        [k in keyof SupplierDefaultValues['defaultPrices']]: yup.AnySchema;
    }>({
        propane: yup.number().min(0),
        oil: yup.number().min(0),
        taxPercentage: yup.number().min(0),
    }),
});

export const OrganizationDefaultValues = ({
    supplierId,
    supplierDefaultValues,
}: OrganizationDefaultValuesProps) => {
    const dispatch = useDispatch();

    const [isSaving, setIsSaving] = useState(false);

    const defaultPricesValues: SupplierDefaultPrices = {
        propane: supplierDefaultValues.defaultPrices?.propane,
        oil: supplierDefaultValues.defaultPrices?.oil,
        taxPercentage: supplierDefaultValues.defaultPrices?.taxPercentage,
    };

    const defaultOrganizationDefaultValues: SupplierDefaultValues = {
        thresh1: supplierDefaultValues.thresh1 ?? 30,
        thresh2: supplierDefaultValues.thresh2 ?? 15,
        tcov: supplierDefaultValues.tcov ?? 5,
        defaultPrices: defaultPricesValues,
    };

    const onSave = (input: SupplierDefaultValues): void => {
        setIsSaving(true);
        updateSupplierDefaultValues(supplierId, input)
            .then(() => {
                setIsSaving(false);
                dispatch(
                    activateSnackbar({
                        message:
                            'Alerts config updated. Changes take effect at next reading.',
                        severity: 'success',
                    })
                );
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    const onError = (error: FieldErrors) => {
        setIsSaving(false);
        dispatch(
            activateSnackbar({
                message: `There was a problem saving this info: ${error}`,
                severity: 'error',
            })
        );
    };

    return (
        <div
            data-testid="OrganizationDefaultValues"
            className={styles.OrganizationDefaultValues}
        >
            <FormProvider
                defaultValues={defaultOrganizationDefaultValues}
                validationSchema={defaultValuesSchema}
            >
                <Card className={styles.card}>
                    <CardHeader title="Default Values" />
                    <CardContent>
                        <div className={styles.formInput}>
                            <FormText
                                field="thresh1"
                                label="Warning (%)"
                                isNumber={true}
                            />
                            <Tooltip
                                title={deviceAlertTooltips['warningThreshold']}
                                enterTouchDelay={0}
                                placement="bottom"
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.formInput}>
                            <FormText
                                field="thresh2"
                                label="Critical (%)"
                                isNumber={true}
                            />
                            <Tooltip
                                title={deviceAlertTooltips['warningThreshold']}
                                enterTouchDelay={0}
                                placement="bottom"
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.formInput}>
                            <FormText
                                field="tcov"
                                label="Change of Tank Level Since Last Transmission (%)"
                                isNumber={true}
                            />
                            <Tooltip
                                title={deviceAlertTooltips['changeOfValue']}
                                enterTouchDelay={0}
                                placement="bottom"
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>

                        <FormInputGroup>
                            <FormText
                                field="defaultPrices.propane"
                                label="Propane $/gal"
                                isNumber={true}
                            />
                        </FormInputGroup>
                        <FormInputGroup>
                            <FormText
                                field="defaultPrices.oil"
                                label="Oil $/gal"
                                isNumber={true}
                            />
                        </FormInputGroup>
                        <FormInputGroup>
                            <FormText
                                field="defaultPrices.taxPercentage"
                                label="Default tax %"
                                isNumber={true}
                            />
                        </FormInputGroup>
                    </CardContent>

                    <div className={styles.saveButtonContainer}>
                        <CardActions>
                            <FormSubmitButton
                                onValidHandler={(
                                    input: SupplierDefaultValues
                                ) => {
                                    onSave(input);
                                }}
                                onErrorHandler={onError}
                                buttonLabel="Save"
                                startIcon={<Save />}
                                isLoading={isSaving}
                            />
                        </CardActions>
                    </div>
                </Card>
            </FormProvider>
        </div>
    );
};
